/**
 * MasterSiteBuilderEdit — Iter 175i (transit page).
 *
 * Master clicks "Edit Site" → MasterSites.js stashes an impersonation
 * token in sessionStorage and navigates to `/master/sites/:siteId/builder`.
 *
 * This page:
 *   1. Verifies the stashed token matches the requested site.
 *   2. Stashes the master's existing localStorage `token` & `user`.
 *   3. Swaps in the impersonation user-token.
 *   4. Redirects to /website-builder/content so the actual WebsiteBuilder
 *      shell takes over. That shell reads `masterImpersonation` from
 *      sessionStorage and shows a "Back to Master" banner across the top.
 *
 * On exit (clicking Back-to-Master inside the builder) we restore the
 * original tokens — see exitMasterImpersonation() in WebsiteBuilderLayout.
 */
import React, { useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Loader2 } from 'lucide-react';

export default function MasterSiteBuilderEdit() {
  const { siteId } = useParams();
  const navigate = useNavigate();

  useEffect(() => {
    try {
      const raw = sessionStorage.getItem('masterImpersonation');
      if (!raw) {
        navigate('/master/sites');
        return;
      }
      const data = JSON.parse(raw);
      if (data.site_id !== siteId) {
        navigate('/master/sites');
        return;
      }
      // Stash master credentials so we can restore them on exit.
      const curToken = localStorage.getItem('token');
      const curUser = localStorage.getItem('user');
      if (!data.original_master_token) {
        data.original_master_token = curToken || '';
        data.original_master_user = curUser || '';
        sessionStorage.setItem('masterImpersonation', JSON.stringify(data));
      }
      localStorage.setItem('token', data.token);
      if (data.user) localStorage.setItem('user', JSON.stringify(data.user));
      // Hand off to the real builder shell. Use a hard reload so the
      // AuthProvider picks up the freshly-swapped token + user.
      window.location.replace('/website-builder/content?master=1');
    } catch (e) {
      navigate('/master/sites');
    }
  }, [siteId, navigate]);

  return (
    <div className="min-h-screen bg-[#0E0E23] text-white flex items-center justify-center" data-testid="master-edit-transit">
      <Loader2 size={28} className="animate-spin text-[#A78BFA]" />
    </div>
  );
}
